/**
 * State + thunks for the Org Settings → Notifications tab.
 *
 * Talks to /api/org/:orgId/notification-prefs via orgNotificationsAPI;
 * server is the authoritative gate (owner-only, see
 * server/routes/org.js's requirePermission('MANAGE_SUBSCRIPTION')).
 *
 * Lives in its own slice so the page's redux state stays narrowly
 * scoped to notifications. Loading + error + per-key saving status are
 * all tracked here so the component is a pure consumer.
 */

import { orgNotificationsAPI } from '../../api';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_PREFS_REQUEST = 'app/OrgSettingsNotificationsPage/FETCH_PREFS_REQUEST';
export const FETCH_PREFS_SUCCESS = 'app/OrgSettingsNotificationsPage/FETCH_PREFS_SUCCESS';
export const FETCH_PREFS_ERROR = 'app/OrgSettingsNotificationsPage/FETCH_PREFS_ERROR';

export const UPDATE_PREF_REQUEST = 'app/OrgSettingsNotificationsPage/UPDATE_PREF_REQUEST';
export const UPDATE_PREF_SUCCESS = 'app/OrgSettingsNotificationsPage/UPDATE_PREF_SUCCESS';
export const UPDATE_PREF_ERROR = 'app/OrgSettingsNotificationsPage/UPDATE_PREF_ERROR';

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  prefs: null,
  fetchInProgress: false,
  fetchLoaded: false,
  fetchError: null,
  savingKey: null,        // key currently being toggled (string) or null
  updateError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_PREFS_REQUEST:
      // On an org switch (different :orgId, same mounted component) drop the
      // previous org's prefs so they can't flash under the new org during its
      // fetch. Same orgId → keep them (stale-while-revalidate).
      return payload.orgId !== state.orgId
        ? { ...initialState, orgId: payload.orgId, fetchInProgress: true }
        : { ...state, orgId: payload.orgId, fetchInProgress: true, fetchError: null };
    case FETCH_PREFS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchLoaded: true,
        prefs: payload.prefs,
      };
    case FETCH_PREFS_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case UPDATE_PREF_REQUEST:
      // Optimistic: apply the new value immediately so the toggle flips
      // without a round-trip. UPDATE_PREF_SUCCESS replaces with the
      // server's view, UPDATE_PREF_ERROR rolls back to `previousValue`.
      return {
        ...state,
        savingKey: payload.key,
        updateError: null,
        prefs: state.prefs ? { ...state.prefs, [payload.key]: payload.value } : state.prefs,
      };
    case UPDATE_PREF_SUCCESS:
      return {
        ...state,
        savingKey: null,
        // Server returns the merged prefs (including defaults); trust it.
        prefs: payload.prefs || state.prefs,
      };
    case UPDATE_PREF_ERROR:
      return {
        ...state,
        savingKey: null,
        updateError: payload.error,
        // Roll back the optimistic update.
        prefs: state.prefs
          ? { ...state.prefs, [payload.key]: payload.previousValue }
          : state.prefs,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchPrefsRequest = orgId => ({ type: FETCH_PREFS_REQUEST, payload: { orgId } });
const fetchPrefsSuccess = prefs => ({ type: FETCH_PREFS_SUCCESS, payload: { prefs } });
const fetchPrefsError = e => ({ type: FETCH_PREFS_ERROR, payload: e });

const updatePrefRequest = (key, value) => ({
  type: UPDATE_PREF_REQUEST,
  payload: { key, value },
});
const updatePrefSuccess = prefs => ({ type: UPDATE_PREF_SUCCESS, payload: { prefs } });
const updatePrefError = (key, previousValue, error) => ({
  type: UPDATE_PREF_ERROR,
  payload: { key, previousValue, error },
});

// ================ Thunks ================ //

/**
 * Fetch the org's current notification prefs from the server.
 * Used by loadData and by anywhere that needs a refresh.
 */
export const fetchNotificationPrefs = orgId => async dispatch => {
  dispatch(fetchPrefsRequest(orgId));
  try {
    const response = await orgNotificationsAPI.getPrefs(orgId);
    // axios returns { data: <body> }; server body is { data: { prefs } }
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(fetchPrefsSuccess(data?.prefs || {}));
    return data?.prefs;
  } catch (e) {
    dispatch(fetchPrefsError(storableError(e)));
    throw e;
  }
};

/**
 * Toggle a single preference. Optimistic — the reducer flips the value
 * immediately, then either confirms with the server's response or
 * rolls back on error.
 */
export const updateNotificationPref = (orgId, key, value, previousValue) => async dispatch => {
  dispatch(updatePrefRequest(key, value));
  try {
    const response = await orgNotificationsAPI.updatePrefs(orgId, { [key]: value });
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(updatePrefSuccess(data?.prefs));
    return data?.prefs;
  } catch (e) {
    dispatch(updatePrefError(key, previousValue, storableError(e)));
    throw e;
  }
};

// ================ loadData ================ //

export const loadData = params => dispatch => {
  const { orgId } = params || {};
  if (!orgId) return Promise.resolve(null);
  return dispatch(fetchNotificationPrefs(orgId));
};
